import React, { useMemo, useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { triggerHaptic } from "@/utils/haptic";

/** Lightweight collapsible wrapper for mobile. Unlike CollapsiblePanel, it has
 *  no inner card border so it can wrap components that already render their
 *  own Card without producing a double-bordered look.
 *
 *  Lazy-renders children only when open ⇒ heavy components (PlateSimulator,
 *  DigitalTwinPanel, MealIntelligence) don't fire useEffect / API calls until
 *  the user expands them. Significant LCP / mobile-data win.
 *
 *  iter327X — `openSignal`: number; whenever it increments, fold force-expands.
 *  Used by external CTAs like PlateSimulator's "Add food" button to expand
 *  and reveal the inner MealLog/search.
 */
export default function MobileFoldSection({
  title,
  icon: Icon,
  color = "#6366F1",
  storageKey,
  defaultOpen = false,
  testId,
  openSignal = 0,
  children,
}) {
  const { isDark } = useTheme();
  const persistKey = storageKey ? `mobile-fold:${storageKey}` : null;
  const initial = useMemo(() => {
    if (!persistKey) return defaultOpen;
    try {
      const v = localStorage.getItem(persistKey);
      return v === null ? defaultOpen : v === "true";
    } catch { return defaultOpen; }
  }, [persistKey, defaultOpen]);
  const [open, setOpen] = useState(initial);

  // External force-open via openSignal increments
  useEffect(() => {
    if (openSignal > 0) {
      setOpen(true);
      if (persistKey) {
        try { localStorage.setItem(persistKey, "true"); } catch { /* ignore */ }
      }
    }
  }, [openSignal, persistKey]);

  const toggle = () => {
    const next = !open;
    // iter315 fix — preserve scroll anchor across state change. Without this,
    // certain mobile browsers (Safari iOS, Android Chrome) can re-evaluate
    // overflow-anchor when an element above current scroll position grows in
    // height, scrolling the viewport upward to a top "natural" anchor point.
    // We snapshot scroll Y, run the state change, then restore on next frame.
    const scrollY = window.scrollY;
    setOpen(next);
    triggerHaptic(next ? 14 : 8);
    if (persistKey) {
      try { localStorage.setItem(persistKey, String(next)); } catch { /* ignore */ }
    }
    // Restore scroll position after browser layout reflow.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (Math.abs(window.scrollY - scrollY) > 4) {
          window.scrollTo({ top: scrollY, behavior: "instant" });
        }
      });
    });
  };

  return (
    <div data-testid={testId} style={{ overflowAnchor: "none" }}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={{ scrollMarginTop: "20px" }}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-2xl border transition-all ${
          isDark
            ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)] hover:border-[hsl(223,47%,28%)]"
            : "bg-white border-gray-100 shadow-sm hover:shadow-md"
        }`}
        data-testid={`${testId}-toggle`}
      >
        <span className="flex items-center gap-2.5 min-w-0 flex-1">
          {Icon && (
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
            >
              <Icon className="w-4 h-4 text-white" />
            </div>
          )}
          <span className={`text-sm font-bold truncate ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {title}
          </span>
        </span>
        {open
          ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
          : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        }
      </button>
      {open && (
        <div className="mt-2" data-testid={`${testId}-content`}>
          {children}
        </div>
      )}
    </div>
  );
}
